/**
 * LegProgressBar Component
 * Displays live progress toward a betting line for player props and game totals.
 *
 * Features:
 * - Line always at 80% width position
 * - Scale max = line × 1.25 (allows showing stats exceeding line)
 * - Color-coded states: Pending (grey), On Track (Over hit line), At Risk (Under), Busted (Under), Hit/Missed (final)
 * - Game time/quarter display
 *
 * Supports:
 * - Player props (pts, reb, ast, etc.)
 * - Game totals (Over/Under combined score)
 *
 * Note: Spreads and moneylines do not use progress bars.
 */

import type { GameStatus, LiveGameScores, LiveLegStats } from '@parlay-picker/shared/hooks';
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import { canonicalizeStatType } from '@parlay-picker/shared/statType';
import type { ReactElement } from 'react';

interface PlayerPropBarProps {
  type: 'player';
  line: number;
  overUnder: 'Over' | 'Under';
  statType: string; // e.g., "pts", "reb", "ast"
  liveStats: LiveLegStats | null;
  settled?: boolean;
  hit?: boolean | null;
}

interface TotalBarProps {
  type: 'total';
  line: number; // e.g., 220.5
  overUnder: 'Over' | 'Under';
  gameScores: LiveGameScores | null;
  settled?: boolean;
  hit?: boolean | null;
}

type LegProgressBarProps = PlayerPropBarProps | TotalBarProps;

type ProgressState =
  | 'pre-game'
  | 'in-progress' // grey — pending, hasn't hit or busted yet
  | 'on-track' // green — Over reached line during live game
  | 'at-risk' // yellow — Under approaching line (80%+)
  | 'busted' // red — Under went over line
  | 'hit' // green — definitively won (settled or final)
  | 'missed'; // red — definitively lost (settled or final)

// =============================================================================
// Helpers
// =============================================================================

/**
 * Get the progress state based on current value vs line
 */
function getProgressState(
  currentValue: number,
  line: number,
  overUnder: 'Over' | 'Under',
  gameStatus: GameStatus,
  settled: boolean,
  hit: boolean | null,
): ProgressState {
  // Already settled - show final result
  if (settled) {
    return hit ? 'hit' : 'missed';
  }

  // Game is final but not yet settled in DB — show definitive result
  if (gameStatus === 'final') {
    if (overUnder === 'Over') {
      return currentValue >= line ? 'hit' : 'missed';
    }
    return currentValue > line ? 'missed' : 'hit';
  }

  // Pre-game, and any status we could not interpret. Falling through to the live
  // branches would claim progress on a game we cannot confirm has started.
  if (gameStatus === 'pre-game' || gameStatus === 'unknown') {
    return 'pre-game';
  }

  // Live Under bets
  if (overUnder === 'Under') {
    if (currentValue > line) return 'busted';
    // At risk when 80%+ of the way to the line (web-specific caution)
    const progress = (currentValue / line) * 100;
    if (progress >= 80) return 'at-risk';
    return 'in-progress'; // grey — safe for now
  }

  // Live Over bets
  if (currentValue >= line) return 'on-track'; // green — reached the line
  return 'in-progress'; // grey — hasn't reached line yet
}

/**
 * Get styling for progress state
 */
function getStateStyles(state: ProgressState): {
  barColor: string;
  lineColor: string;
  labelColor: string;
  badge: { text: string; bg: string } | null;
} {
  switch (state) {
    case 'pre-game':
      return {
        barColor: 'bg-theme-border',
        lineColor: 'bg-theme-text-tertiary',
        labelColor: 'text-theme-text-tertiary',
        badge: null,
      };
    case 'in-progress':
      return {
        barColor: 'bg-theme-text-tertiary',
        lineColor: 'bg-white',
        labelColor: 'text-theme-text-secondary',
        badge: null,
      };
    case 'on-track':
      return {
        barColor: 'bg-theme-success',
        lineColor: 'bg-white',
        labelColor: 'text-theme-success',
        badge: null,
      };
    case 'at-risk':
      return {
        barColor: 'bg-theme-warning',
        lineColor: 'bg-white',
        labelColor: 'text-theme-warning',
        badge: { text: 'AT RISK', bg: 'bg-theme-warning' },
      };
    case 'busted':
      return {
        barColor: 'bg-theme-danger',
        lineColor: 'bg-white',
        labelColor: 'text-theme-danger',
        badge: { text: 'BUSTED', bg: 'bg-theme-danger' },
      };
    case 'hit':
      return {
        barColor: 'bg-theme-success',
        lineColor: 'bg-white',
        labelColor: 'text-theme-success',
        badge: { text: 'HIT', bg: 'bg-theme-success' },
      };
    case 'missed':
      return {
        barColor: 'bg-theme-danger',
        lineColor: 'bg-white',
        labelColor: 'text-theme-danger',
        badge: { text: 'MISSED', bg: 'bg-theme-danger' },
      };
  }
}

/**
 * Format quarter display
 */
function formatQuarter(quarter: number | null): string {
  if (quarter === null) return '';
  if (quarter === 1) return 'Q1';
  if (quarter === 2) return 'Q2';
  if (quarter === 3) return 'Q3';
  if (quarter === 4) return 'Q4';
  if (quarter >= 5) return `OT${quarter - 4}`;
  return '';
}

/**
 * Format stat type for display with the shared stat labels ("Points", "PRA")
 * Strips "_alternate" suffix - the alternate nature is indicated by the line (X+ format)
 */
function formatStatTypeDisplay(statType: string): string {
  // Strip _alternate suffix - alternate props are distinguished by line format (X+), not stat label
  let normalized = statType.toLowerCase();
  if (normalized.endsWith('_alternate')) {
    normalized = normalized.slice(0, -10);
  }
  return formatStatLabel(canonicalizeStatType(normalized));
}

// =============================================================================
// Component
// =============================================================================

export default function LegProgressBar(props: LegProgressBarProps): ReactElement {
  // Extract common values based on type
  const isTotal = props.type === 'total';

  const currentValue = isTotal
    ? (props.gameScores?.homeScore ?? 0) + (props.gameScores?.awayScore ?? 0)
    : (props.liveStats?.currentStat ?? 0);

  const gameStatus = isTotal
    ? (props.gameScores?.gameStatus ?? 'pre-game')
    : (props.liveStats?.gameStatus ?? 'pre-game');

  const quarter = isTotal
    ? (props.gameScores?.quarter ?? null)
    : (props.liveStats?.quarter ?? null);

  const timeRemaining = isTotal
    ? (props.gameScores?.timeRemaining ?? null)
    : (props.liveStats?.timeRemaining ?? null);

  const { line, overUnder, settled = false, hit = null } = props;

  // Calculate state
  const state = getProgressState(currentValue, line, overUnder, gameStatus, settled, hit);
  const styles = getStateStyles(state);

  // Final but unsettled games: show grey FINAL badge instead of HIT/MISSED
  const displayBadge =
    !settled && gameStatus === 'final'
      ? { text: 'FINAL', bg: 'bg-theme-surface-active' }
      : styles.badge;

  // Calculate bar positions
  // Line is always at 80%, max scale = line × 1.25
  const maxScale = line * 1.25;
  const linePosition = 80; // Line at 80%
  const currentPosition = Math.min((currentValue / maxScale) * 100, 100);

  // Format display
  const valueLabel = isTotal
    ? `${currentValue} pts`
    : `${currentValue} ${formatStatTypeDisplay((props as PlayerPropBarProps).statType)}`;
  const quarterDisplay = formatQuarter(quarter);
  const timeDisplay = timeRemaining ?? '';

  return (
    <div className="w-full">
      {/* Progress bar container */}
      <div className="relative h-3 bg-theme-surface-hover rounded-full overflow-hidden">
        {/* Progress fill */}
        <div
          className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ${styles.barColor}`}
          style={{ width: `${currentPosition}%` }}
        />

        {/* Line marker */}
        <div
          className={`absolute top-0 w-0.5 h-full ${styles.lineColor}`}
          style={{ left: `${linePosition}%` }}
        />
      </div>

      {/* Labels row */}
      <div className="flex items-center justify-between mt-1 text-xs">
        <div className={`font-medium ${styles.labelColor}`}>{valueLabel}</div>

        <div className="flex items-center gap-2">
          {/* Line value */}
          <span className="text-theme-text-tertiary">{line}</span>

          {/* Status badge */}
          {displayBadge && (
            <span
              className={`px-1.5 py-0.5 rounded text-[10px] font-bold text-white ${displayBadge.bg}`}
            >
              {displayBadge.text}
            </span>
          )}
        </div>
      </div>

      {/* Game time row (if live) */}
      {gameStatus === 'live' && (quarterDisplay || timeDisplay) && (
        <div className="flex justify-end text-[10px] text-theme-text-tertiary">
          {quarterDisplay} {timeDisplay}
        </div>
      )}
    </div>
  );
}
